import React, { useCallback, useMemo, useEffect, useState } from 'react';
import { useExpanded, useTable } from 'react-table';

import { ChevronSmallDown as ChevronSmallDownIcon } from '@styled-icons/entypo/ChevronSmallDown';
import { ChevronSmallRight as ChevronSmallRightIcon } from '@styled-icons/entypo/ChevronSmallRight';
import GroupIcon from 'mdi-react/GroupIcon';
import InfoIcon from 'mdi-react/InfoCircleOutlineIcon';
import { api } from 'services/api';
import { toast } from 'shared/toast';

import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  HStack,
  Icon,
  Box,
  SimpleGrid,
  Select,
  Flex,
} from '@chakra-ui/react';

import Loader from 'components/Loader';

import ElementPreviewRow from './ElementPreviewTableRow';
import ImportStatusBadge from './StatusBadge';
import { ImportPreview, Row } from './types';

type Props = {
  importId: number;
  parentStage: number;
};

type Status = '*' | 'error' | 'warning' | 'success';

const ElementPreviewTable: React.FC<Props> = ({ importId, parentStage }) => {
  const [importPreview, setImportPreview] = useState<ImportPreview[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<Status>('*');

  const getImportPreview = useCallback(async () => {
    setLoading(true);
    try {
      const { data: responseData } = await api.get(
        `/import/${importId}/preview`,
        {
          params: {
            page: 1,
            per_page: 9999,
            'filter[status]': status === '*' ? undefined : status,
            'filter[parent_stage]': parentStage,
            'filter[_index]': 'import_element.*',
          },
        },
      );
      setImportPreview(responseData.data);
    } catch (err) {
      toast({
        description:
          err.response?.data?.message ||
          'Houve um erro ao obter o preview do arquivo.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [importId, status, parentStage]);

  useEffect(() => {
    getImportPreview();
  }, [getImportPreview]);

  const columns = useMemo(
    () => [
      {
        id: 'expander',
        Header: 'Tipo',
        width: '1%',
        Cell: ({ row }: { row: Row }) => {
          return (
            <HStack spacing={2}>
              {row.original._source.raw_data.type === 'budget_stage' &&
                (row.isExpanded ? (
                  <Icon w={4} h={4} as={ChevronSmallDownIcon} />
                ) : (
                  <Icon w={4} h={4} as={ChevronSmallRightIcon} />
                ))}

              <Flex
                alignItems="center"
                justifyContent="center"
                flexDirection="column"
                gap={1}
              >
                <Text
                  fontSize="sm"
                  textTransform="uppercase"
                  whiteSpace="nowrap"
                  fontWeight="bold"
                >
                  {row.original._source.raw_data.item || null}
                </Text>

                {row.original._source.raw_data.type ===
                  'budget_composition' && (
                  <Box title="Composição">
                    <Icon w={5} h={5} as={GroupIcon} />
                  </Box>
                )}

                {row.original._source.raw_data.type === 'budget_input' && (
                  <Box title="Insumo">
                    <Icon w={5} h={5} as={InfoIcon} />
                  </Box>
                )}
              </Flex>
            </HStack>
          );
        },
      },
      {
        Header: 'Base',
        width: '5%',
        Cell: ({ row }: { row: Row }) => {
          return row.original._source.raw_data.type === 'budget_stage' ? (
            <Text
              maxWidth={{ base: '100%', md: '768px' }}
              whiteSpace="pre-wrap"
            >
              {row.original._source.raw_data.description || '-'}
            </Text>
          ) : (
            row.original._source.raw_data.base || '-'
          );
        },
      },
      {
        Header: 'Código',
        width: '5%',
        Cell: ({ row }: { row: Row }) => {
          return (
            row.original._source.raw_data.code ||
            (row.original._source.raw_data.type === 'budget_stage' ? null : '-')
          );
        },
      },
      {
        Header: 'Descrição',
        width: '40%',
        Cell: ({ row }: { row: Row }) => {
          return (
            <Text
              maxWidth={{ base: '100%', md: '768px' }}
              whiteSpace="pre-wrap"
            >
              {row.original._source.raw_data.type === 'budget_stage'
                ? null
                : row.original._source.raw_data.description || '-'}
            </Text>
          );
        },
      },
      {
        Header: 'Coeficiente',
        width: '5%',
        isNumeric: true,
        Cell: ({ row }: { row: Row }) => {
          return row.original._source.raw_data.coefficient || '-';
        },
      },
      {
        Header: 'Linha do arquivo',
        width: '1%',
        isNumeric: true,
        Cell: ({ row }: { row: Row }) => {
          return row.original._source.index;
        },
      },
      {
        Header: 'Status',
        width: '1%',
        Cell: ({ row }: { row: Row }) => {
          return <ImportStatusBadge status={row.original._source.status} />;
        },
      },
      {
        Header: 'Erros',
        width: '20%',
        Cell: ({ row }: { row: Row }) => {
          return (
            row.original._source?.message && (
              <Text py={1} whiteSpace="pre-wrap">
                {Object.keys(row.original._source?.message).length > 0
                  ? Object.values(row.original._source?.message).join(', ')
                  : '-'}
              </Text>
            )
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const getRowId = useCallback((row) => {
    return row._id;
  }, []);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    visibleColumns,
  } = useTable(
    {
      columns,
      data: importPreview,
      getRowId,
    },
    useExpanded,
  );

  const renderRowSubComponent = useCallback(
    ({ parentStage: nextParentStage }) => (
      <ElementPreviewTable parentStage={nextParentStage} importId={importId} />
    ),
    [importId],
  );

  return (
    <>
      <SimpleGrid columns={{ base: 1, md: 2 }}>
        <HStack mb={3} />

        <HStack mb={3} display="flex" justifyContent="flex-end">
          <Text fontSize="smaller" color="white">
            Status:
          </Text>
          <Select
            width="auto"
            defaultValue="*"
            onChange={(e) => {
              setStatus(e.target.value as Status);
            }}
          >
            {[
              ['*', 'Todos'],
              ['success', 'Sucesso'],
              ['warning', 'Atenção'],
              ['error', 'Erro'],
            ].map(([key, description]) => (
              <option key={key} value={key}>
                {description}
              </option>
            ))}
          </Select>
        </HStack>
      </SimpleGrid>

      <TableContainer width="100%">
        <Table {...getTableProps()} borderBottom="none" variant="simple">
          <Thead>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <Th
                    {...column.getHeaderProps()}
                    width={column.width}
                    // @ts-expect-error isNumeric does not exists on lib type
                    isNumeric={column.isNumeric}
                  >
                    {column.render('Header')}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          <Tbody {...getTableBodyProps()}>
            {!loading &&
              rows.length > 0 &&
              rows.map((row) => {
                prepareRow(row);

                return (
                  <ElementPreviewRow
                    row={row as unknown as Row}
                    visibleColumns={visibleColumns}
                    renderRowSubComponent={renderRowSubComponent}
                    {...row.getRowProps()}
                  />
                );
              })}

            {loading && (
              <Tr>
                <Td
                  colSpan={visibleColumns.length}
                  borderBottom="none"
                  background="gray.100"
                >
                  <Loader />
                </Td>
              </Tr>
            )}

            {!loading && importPreview.length === 0 && (
              <Tr>
                <Td
                  colSpan={visibleColumns.length}
                  borderBottom="none"
                  background="gray.100"
                  className="text-center"
                >
                  Nenhum dado encontrado
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
    </>
  );
};

export default ElementPreviewTable;
